import {z} from 'zod'

export const OrganizationListItemSchema = z
    .object({
        id: z.string(),
        name: z.string(),
        created_at: z.string(),
        deactivated_at: z.nullable(z.string()),
    })
    .transform(({created_at, deactivated_at, ...rest}) => ({
        ...rest,
        createdAt: created_at,
        deactivatedAt: deactivated_at,
    }))
export const OrganizationListItemResponseSchema = z.array(OrganizationListItemSchema)
export type OrganizationListItem = z.infer<typeof OrganizationListItemSchema>
export type OrganizationListItemResponseInput = z.input<typeof OrganizationListItemSchema>
export type OrganizationListItemResponse = z.infer<typeof OrganizationListItemResponseSchema>

export const GiftTableRowSchema = z
    .object({
        gift_size: z.number(),
        number_required: z.nullable(z.number()),
        sort_order: z.number(),
    })
    .transform(({gift_size, number_required, sort_order}) => ({
        giftSize: gift_size,
        numberRequired: number_required,
        sortOrder: sort_order,
    }))
export type GiftTableRow = z.infer<typeof GiftTableRowSchema>

export const CreateOrganizationRequestSchema = z
    .object({
        name: z.string(),
        campaignGoal: z.number().positive(),
        toplinePercentage: z.number().min(15).max(25),
        useRfmForEngagement: z.boolean(),
    })
    .transform(({name, campaignGoal, toplinePercentage, useRfmForEngagement}) => ({
        name,
        campaign_goal: campaignGoal,
        topline_percentage: toplinePercentage,
        use_rfm_for_engagement: useRfmForEngagement,
    }))
export type CreateOrganizationRequest = z.input<typeof CreateOrganizationRequestSchema>
export type CreateOrganizationRequestApi = z.output<typeof CreateOrganizationRequestSchema>

export const UpdateOrganizationRequestSchema = z
    .object({
        name: z.string().optional(),
        campaignGoal: z.number().positive().optional(),
        toplinePercentage: z.number().min(15).max(25).optional(),
        useRfmForEngagement: z.boolean().optional(),
    })
    .transform(({name, campaignGoal, toplinePercentage, useRfmForEngagement}) => ({
        ...(name !== undefined && {name}),
        ...(campaignGoal !== undefined && {campaign_goal: campaignGoal}),
        ...(toplinePercentage !== undefined && {topline_percentage: toplinePercentage}),
        ...(useRfmForEngagement !== undefined && {use_rfm_for_engagement: useRfmForEngagement}),
    }))
export type UpdateOrganizationRequest = z.input<typeof UpdateOrganizationRequestSchema>
export type UpdateOrganizationRequestApi = z.output<typeof UpdateOrganizationRequestSchema>

export const OrganizationDetailSchema = z
    .object({
        id: z.string(),
        name: z.string(),
        created_at: z.string(),
        deactivated_at: z.nullable(z.string()),
        user_count: z.number(),
        prospect_count: z.number(),
        gift_table_rows: z.array(GiftTableRowSchema),
        campaign_goal: z.number(),
        topline_percentage: z.number(),
        use_rfm_for_engagement: z.boolean(),
    })
    .transform(
        ({
            created_at,
            deactivated_at,
            user_count,
            prospect_count,
            gift_table_rows,
            campaign_goal,
            topline_percentage,
            use_rfm_for_engagement,
            ...rest
        }) => ({
            ...rest,
            createdAt: created_at,
            deactivatedAt: deactivated_at,
            userCount: user_count,
            prospectCount: prospect_count,
            giftTableRows: gift_table_rows,
            campaignGoal: campaign_goal,
            toplinePercentage: topline_percentage,
            useRfmForEngagement: use_rfm_for_engagement,
        }),
    )
export type OrganizationDetail = z.infer<typeof OrganizationDetailSchema>
export type OrganizationDetailResponseInput = z.input<typeof OrganizationDetailSchema>

export const UploadEventListItemSchema = z
    .object({
        id: z.string(),
        study_id: z.string(),
        file_name: z.string(),
        record_count: z.number(),
        uploaded_by_name: z.string(),
        created_at: z.string(),
    })
    .transform(({study_id, file_name, record_count, uploaded_by_name, created_at, ...rest}) => ({
        ...rest,
        studyId: study_id,
        fileName: file_name,
        recordCount: record_count,
        uploadedByName: uploaded_by_name,
        createdAt: created_at,
    }))
export const UploadEventListItemResponseSchema = z.array(UploadEventListItemSchema)
export type UploadEventListItem = z.infer<typeof UploadEventListItemSchema>
export type UploadEventListItemResponseInput = z.input<typeof UploadEventListItemSchema>
export type UploadEventListItemResponse = z.infer<typeof UploadEventListItemResponseSchema>

export const UploadTemplateHeadersSchema = z.object({
    headers: z.array(z.string()),
})
export type UploadTemplateHeaders = z.infer<typeof UploadTemplateHeadersSchema>
export type UploadTemplateHeadersResponseInput = z.input<typeof UploadTemplateHeadersSchema>

export const UploadProspectsResponseSchema = z
    .object({
        study_id: z.string(),
        record_count: z.number(),
        upload_event_id: z.string(),
    })
    .transform(({study_id, record_count, upload_event_id}) => ({
        studyId: study_id,
        recordCount: record_count,
        uploadEventId: upload_event_id,
    }))
export type UploadProspectsResponse = z.infer<typeof UploadProspectsResponseSchema>
export type UploadProspectsResponseInput = z.input<typeof UploadProspectsResponseSchema>

const previewRowSchema = z.array(z.tuple([z.string(), z.string()]))

export const CsvPreviewResponseSchema = z
    .object({
        record_count: z.number(),
        preview_rows: z.array(previewRowSchema),
        unrecognized_columns: z.array(z.string()).optional(),
    })
    .transform(({record_count, preview_rows, unrecognized_columns}) => ({
        recordCount: record_count,
        previewRows: preview_rows,
        unrecognizedColumns: unrecognized_columns ?? [],
    }))
export type CsvPreviewResponse = z.infer<typeof CsvPreviewResponseSchema>
